import React, { useMemo } from "react";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";

// Curated list of professional YouTube videos for portfolios
const VIDEO_SUGGESTIONS = [
  {
    url: "https://www.youtube.com/watch?v=yNLdblFQqsw",
    title: "Our Narrow Slice",
    category: "Entertainment",
  },
  {
    url: "https://www.youtube.com/watch?v=jNQXAC9IVRw",
    title: "Me at the Zoo",
    category: "Classic",
  },
  {
    url: "https://www.youtube.com/watch?v=XX7PdJIGiCw&t=2s",
    title: "The Selfish Gene",
    category: "Education",
  },
  {
    url: "https://www.youtube.com/watch?v=VsRmyY3Db1Y&t=266s",
    title: "Milky Way the Size of the US",
    category: "Education",
  },
  {
    url: "https://www.youtube.com/watch?v=fHRS_NOs24w&t=1642s",
    title: "The Indian Genius",
    category: "Education",
  },
  {
    url: "https://www.youtube.com/watch?v=up6NRP3cj4Y",
    title: "Why should you meditate?",
    category: "Meditation",
  },
  {
    url: "https://www.youtube.com/watch?v=7wtfhZwyrcc",
    title: "Avicii - Wake Me Up",
    category: "Music",
  },
  {
    url: "https://www.youtube.com/watch?v=RgKAFK5djSk",
    title: "Wiz Khalifa - See You Again",
    category: "Music",
  },
];

/**
 * Extract YouTube video ID from various YouTube URL formats
 * Supports:
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
function extractYouTubeId(url) {
  if (!url) return null;
  
  // Regular YouTube watch URL
  let match = url.match(/[?&]v=([^&]+)/);
  if (match) return match[1];
  
  // Short youtu.be URL
  match = url.match(/youtu\.be\/([^?]+)/);
  if (match) return match[1];
  
  // Embed URL
  match = url.match(/youtube\.com\/embed\/([^?]+)/);
  if (match) return match[1];
  
  return null;
}

/**
 * Get embed URL from a video ID
 */
function getEmbedUrl(videoId) {
  if (!videoId) return null;
  return `https://www.youtube.com/embed/${videoId}`;
}

export default function VideoUrlField({ label, help, value, onChange }) {
  // Extract video ID from current value
  const currentVideoId = useMemo(() => extractYouTubeId(value), [value]);
  const embedUrl = useMemo(() => getEmbedUrl(currentVideoId), [currentVideoId]);
  
  const handleSuggestionClick = (url) => {
    onChange(url);
  };
  
  const handleInputChange = (e) => {
    onChange(e.target.value);
  };
  
  const handleRemoveVideo = () => {
    onChange("");
  };
  
  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
        </label>
      )}
      
      {/* URL Input with Remove Button */}
      <div className="mb-3">
        <div className="flex gap-2">
          <input
            type="text"
            value={value || ""}
            onChange={handleInputChange}
            placeholder="https://www.youtube.com/watch?v=..."
            className={`flex-1 px-4 py-2.5 border rounded-xl ${typography.body} ${textColors.primary} ${surfaces.primary} ${borders.default} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 placeholder:${textColors.muted} transition-all duration-200`}
          />
          {value && (
            <button
              type="button"
              onClick={handleRemoveVideo}
              className={`px-4 py-2.5 border rounded-xl transition-colors ${typography.button} ${textColors.primary} ${borders.default} ${surfaces.tertiary} ${surfaces.hover}`}
              title="Remove video"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      
      {/* Video Preview */}
      {embedUrl && (
        <div className="mb-3">
          <div className={`mb-2 ${typography.caption} ${textColors.secondary}`}>
            Preview:
          </div>
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src={embedUrl}
              title="Video preview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className={`absolute top-0 left-0 w-full h-full rounded border ${borders.default}`}
            />
          </div>
        </div>
      )}
      
      {/* Video Suggestions Section */}
      <div className="mb-3">
        <div className={`mb-2 ${typography.caption} ${textColors.secondary}`}>
          Suggested Videos:
        </div>
        <div className="grid grid-cols-2 gap-2">
          {VIDEO_SUGGESTIONS.map((suggestion, idx) => {
            const suggestionId = extractYouTubeId(suggestion.url);
            const suggestionEmbedUrl = getEmbedUrl(suggestionId);
            const isSelected = suggestion.url === value;
            
            return (
              <button
                key={idx}
                type="button"
                onClick={() => handleSuggestionClick(suggestion.url)}
                className={`flex flex-col p-2 border rounded transition-all text-left ${
                  isSelected
                    ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
                    : `${borders.default} ${surfaces.secondary} ${surfaces.hover}`
                }`}
              >
                {/* Video Thumbnail */}
                <div className="relative w-full mb-2" style={{ paddingBottom: "56.25%" }}>
                  {suggestionEmbedUrl && (
                    <img
                      src={`https://img.youtube.com/vi/${suggestionId}/mqdefault.jpg`}
                      alt={suggestion.title}
                      className="absolute top-0 left-0 w-full h-full object-cover rounded"
                    />
                  )}
                </div>
                
                {/* Video Info */}
                <div className={`${typography.caption} ${isSelected ? "text-white" : textColors.primary} font-medium mb-1 line-clamp-2`}>
                  {suggestion.title}
                </div>
                <div className={`${typography.caption} ${isSelected ? "text-white/70" : textColors.tertiary}`}>
                  {suggestion.category}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      
      {help && (
        <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      )}
    </div>
  );
}

